/*eslint-disable @typescript-eslint/member-ordering */

import { DeferrableFilter, FilterTemplate, MultiSelectFilter, SelectFilter, StringFilter, TimeFilter } from '@models/filter-types';
import { DaypartLookupItem, LookupItem, SegmentDOWLookupItem, SegmentItem, StringLookupItem } from '../lookup';
import { Segmentation, UiConfig } from '@models/config';
import { cascadeToSingleFilter, generateSingleSelectFiltersWithSideText } from '@shared/helpers/functions/filter-helpers';
import { UrlStore } from '@shared/helpers/constants/url-store';
import { LookupV2Service } from '@services/lookup-v2/lookup-v2.service';
import { daypartsSingleFilter, startDateFilter, rateFilter, endDateFilter } from '@models/base_filters';
import { FilterSettings } from '@models/filter';
import { computeFlightWeeks, dateFormatter } from '@shared/helpers/functions/helpers';
import { take } from 'rxjs/operators';
import { of } from 'rxjs';
import { Moment } from 'moment';
import moment from 'moment';
import { EditableSegment } from '@models/rate-card/rate-card';

export class SegmentRecurringFilters implements FilterTemplate {
    public loading!: boolean;
    public primaryRateValue?: SelectFilter<StringLookupItem>;
    public tierValue?: number[] = [];
    public changeDates?: boolean = true;
    public rateCardTabsData?: number = 1;
    public savedReportLoaded = false;
    public showMetrics!: boolean;

    public dynamicFilters: { [segmentType: string]: SelectFilter<SegmentItem> | StringFilter | TimeFilter } = {};

    constructor(
        private lookupSvc: LookupV2Service,
        public config: UiConfig,
        private tabNumber: number,
        private channelGroupId: number,
        private channelId: number,
        private spotType: number,
        private daypartId: number,
        private priceFloor: number,
        private priceMax: number,
        private primarySpotLength: number,
        private primaryTier: string,
        private segmentData?: EditableSegment,
    ) {
        this.initDynamicSegmentationFilters();
    }

    settings: FilterSettings = {
        config: this.config,
        lookupSvc: this.lookupSvc,
        hasDynamicRates: true,
        tabNumber: this.tabNumber,
        screenName: UrlStore.screenNames.rateCard,
    };

    dayparts = daypartsSingleFilter(this.settings)
        .Defer(() => this.lookupSvc.getDayparts([this.channelId], [this.spotType], this.settings))
        .OnLoad((items: DaypartLookupItem[]) => {
            if (this.segmentData?.daypartId) {
                this.dayparts.Value = items.find((item) => item.id === this.segmentData.daypartId);
            } else {
                this.dayparts.Value = items.find((item) => item.id === this.daypartId);
            }
        })
        .OnChange((item: LookupItem) => {
            if (this.anyFilterOpen() || !item) {
                return;
            }
            const segmentation = this.config.groupSegmentation[this.channelGroupId]?.rate_card;

            if (segmentation?.length) {
                const firstSegmentation = segmentation.sort((first, second) => first.segment_order - second.segment_order)[0];

                const firstFilter = this.dynamicFilters[firstSegmentation.segment_type] as SelectFilter<SegmentItem>;
                if (!firstFilter) {
                    return;
                }
                firstFilter.isLoading = true;
                const channelIdsSelected = [this.channelId];
                const daypartIdsSelected = item.id;
                this.lookupSvc
                    .getRateCardAddRowPopupSegments(channelIdsSelected, [daypartIdsSelected], firstSegmentation)
                    .pipe(take(1))
                    .subscribe(
                        (result) => {
                            cascadeToSingleFilter(
                                firstFilter,
                                result,
                                this.segmentData?.dynamicSegmentation[firstSegmentation.segment_type],
                                false,
                            );
                        },
                        (err) => {
                            console.error(err);
                            firstFilter.isLoading = false;
                            firstFilter.loadError = 'Failed to Load';
                        },
                    );
            }
        })
        .IsDynamicFilterParent(true)
        .Required(true);

    startDate = startDateFilter()
        .Default(this.segmentData?.recurringSegment.startDate ? moment(this.segmentData?.recurringSegment.startDate) : undefined)
        .OnChange((dateVal) => {
            this.endDate.startAt(dateVal);
            this.resetFlightWeeks();
        })
        .Validator(
            (v: Moment) => (v && this.endDate.Value ? v.isBefore(this.endDate.Value) : true),
            () => 'Start date must be before end date',
        )
        .Required(true)
        .Filter((v: moment.Moment) => v >= moment().startOf('day'));

    endDate = endDateFilter()
        .Default(this.segmentData?.recurringSegment.endDate ? moment(this.segmentData?.recurringSegment.endDate) : undefined)
        .OnChange((_) => {
            this.resetFlightWeeks();
            this.startDate.Validate();
        })
        .Required(true)
        .Filter((v: moment.Moment) => {
            if (!this.startDate.Value) {
                return false;
            } else {
                return v > this.startDate.Value;
            }
        });

    // this isn't really correct, as the dow list should be static, but this is good enough for now
    dow = new SelectFilter<SegmentDOWLookupItem>('Day(s) of Week (Select one option)')
        .DefaultName('')
        .Defer(() => this.lookupSvc.getSegmentDOW())
        .OnLoad(() => {
            if (this.segmentData?.recurringSegment.dows) {
                const savedIds = this.segmentData?.recurringSegment.dows;
                this.dow.Value = this.dow.options.find(
                    (option) => option.ids.every((id) => savedIds.includes(id)) && savedIds.every((id) => option.ids.includes(id)),
                );
            }
        })
        .Filterable(true)
        .Required(true);

    rateFilter = rateFilter(this.priceFloor, this.priceMax, this.primarySpotLength, this.primaryTier)
        .Hint('OPTIONAL')
        .Default(this.segmentData?.rate)
        .Prefix('$')
        .Toggle(!this.primaryTier || !this.primarySpotLength);

    hiatusWeeks = new MultiSelectFilter<string>('Hiatus Weeks (OPTIONAL)')
        .Defer(() => of(this.segmentData?.recurringSegment.hiatusWeeks || []))
        .Default(this.segmentData?.recurringSegment.hiatusWeeks || [])
        .Clear(() => (this.hiatusWeeks.Value = []))
        .TriggerText((o, s) => (o.length === s.length ? 'All ' + 'Hiatus Weeks' : `${s.length} ` + 'Hiatus Weeks' + ' Selected'))
        .TriggerLength(3)
        .Transform((v: string[]) => v.map((week) => dateFormatter(week, 'yyyy/MM/dd')))
        .RestoreWithId(false)
        .Validator(
            (selected: string[]): boolean =>
                this.hiatusWeeks.options.length > 0 ? selected.length < this.hiatusWeeks.options.length : true,
            () => 'Must have at least one non-hiatus week',
        )
        .Icon('fas fa-calendar-xmark');

    resetFlightWeeks(): void {
        const prevSelected = this.hiatusWeeks.options.filter((option) => this.hiatusWeeks.Value.includes(option));
        this.hiatusWeeks.Options(computeFlightWeeks(this.startDate.Value, this.endDate.Value));
        this.hiatusWeeks.Value = this.hiatusWeeks.options.filter((option) => prevSelected.includes(option));
    }

    initDynamicSegmentationFilters(): void {
        const groupSegmentation = this.config.groupSegmentation[this.channelGroupId]?.rate_card || [];
        this.dynamicFilters = generateSingleSelectFiltersWithSideText(
            () => [this.channelId],
            () => [this.daypartId],
            groupSegmentation.filter((x) => !x.is_dow),
            (
                channelIds: number[],
                daypartIds: number[],
                segmentation: Segmentation,
                filterValues: { [segmentType: string]: (string | number)[] },
                clearCache?: boolean,
            ) => this.lookupSvc.getRateCardAddRowPopupSegments(channelIds, daypartIds, segmentation, filterValues, clearCache),
            this.segmentData?.dynamicSegmentation,
        );
    }

    anyFilterOpen: () => boolean = () =>
        Object.keys(this)
            .filter((key) => this[key as keyof SegmentRecurringFilters] instanceof DeferrableFilter)
            .filter((key) => (this[key as keyof SegmentRecurringFilters] as DeferrableFilter<unknown, unknown>).isOpened).length > 0;
}
